import React, { useState } from 'react'
import { TariffUpgradeType } from '../../methods/TariffUpgradeType'
import { applyDiscountToPrice } from '../../util/priceCalculation'
import { firstInstallmentKopecks, isInstallmentPackage } from '../../util/installment'
import CustomQuantitySlider from './CustomQuantitySlider'
import Popup from '../../components/Popup'
import InfoButton from '../../components/InfoButton'
import 'pages/Styles/TariffCard.sass'

type TariffDisplayData = {
    name: string
    badge: string
    period: string
    totalPrice: string
    pricePerDocument: string
}

type Props = {
    tariffs: any[]
    onAddToCart: (item: any) => void
    cart: any[]
}

function TariffCards({ tariffs, onAddToCart, cart }: Props) {
    const [showInfoPopup, setShowInfoPopup] = useState(false)

    function formatPrice(price: number): string {
        // Проверяем, является ли число целым (с учетом погрешности чисел с плавающей точкой)
        if (Math.abs(price % 1) < 0.0001) {
            return price.toFixed(0)
        }
        return price.toFixed(2)
    }

    function isInCart(id: string) {
        return cart.some(item => item._id === id)
    }

    function getCustomPackageData() {
        const customPackage = cart.find(item => item._id === 'custom')
        return customPackage
    }

    function getMaxDiscount(): number {
        // Находим максимальную скидку среди всех тарифов
        if (tariffs.length === 0) {
            return 0
        }
        return Math.max(...tariffs.map(tariff => tariff.discount || 0))
    }

    function getMinDiscount(): number {
        // Находим минимальную скидку среди всех тарифов (исключая 0)
        if (tariffs.length === 0) {
            return 0
        }
        const discounts = tariffs
            .map(tariff => tariff.discount || 0)
            .filter(discount => discount > 0)
        if (discounts.length === 0) {
            return 0
        }
        return Math.min(...discounts)
    }

    function createDisplayDataFromTariff(tariff: any): TariffDisplayData {
        const discount = tariff.discount || 0
        const originalPrice = tariff.price
        const finalPrice = applyDiscountToPrice(originalPrice, discount)
        const pricePerDoc = finalPrice / tariff.prepaid_documents_count / 100
        
        let totalPriceDisplay = `${(finalPrice / 100).toFixed(0)} ₽`
        if (discount > 0) {
            totalPriceDisplay = `${(finalPrice / 100).toFixed(0)} ₽`
        }
        
        return {
            name: tariff.name,
            badge: `${tariff.prepaid_documents_count} договоров`,
            period: tariff.period === 'year' ? '1 год' : '1 месяц',
            totalPrice: totalPriceDisplay,
            pricePerDocument: `${formatPrice(pricePerDoc)} ₽ за 1 договор`
        }
    }

    function createArtificialTariff(baseTariff: any, customData?: any) {
        if (!baseTariff) {
            return null
        }
        // Если количество не выбрано, используем минимальную скидку и оригинальную цену минимального тарифа
        // Если выбрано, используем реальную скидку из customData
        let discount = 0
        let price = 0
        let isMinDiscount = false
        
        if (customData) {
            discount = customData.discount || 0
            price = customData.price || 0
            isMinDiscount = customData.isMinDiscount !== undefined ? customData.isMinDiscount : false
        } else {
            // Находим тариф с минимальной ценой (оригинальной, без скидки)
            const minPriceTariff = tariffs.length > 0 ? tariffs.reduce((min, tariff) => {
                return tariff.price < min.price ? tariff : min
            }) : null
            
            if (minPriceTariff) {
                price = minPriceTariff.price
                discount = getMinDiscount()
                isMinDiscount = true
            }
        }
        
        return {
            _id: "custom",
            name: "На выбор",
            type: "package_v2",
            prepaid_documents_count: customData ? customData.prepaid_documents_count : baseTariff.prepaid_documents_count,
            price: price,
            discount: discount,
            period: baseTariff.period,
            upgrade_additional_price: baseTariff.upgrade_additional_price,
            is_active: true,
            is_default: false,
            isMinDiscount: isMinDiscount // Флаг, что это минимальная скидка (от -X%)
        }
    }

    function createDisplayDataFromCustom(customData: any | null, baseTariff: any): TariffDisplayData {
        if (customData) {
            const discount = customData.discount || 0
            const originalPrice = customData.price
            const finalPrice = applyDiscountToPrice(originalPrice, discount)
            const pricePerDoc = (finalPrice / customData.prepaid_documents_count) / 100
            
            return {
                name: 'На выбор',
                badge: `${customData.prepaid_documents_count} договоров`,
                period: baseTariff?.period === 'year' ? '1 год' : '1 месяц',
                totalPrice: `${Math.round(finalPrice / 100)} ₽`,
                pricePerDocument: `${formatPrice(pricePerDoc)} ₽ за 1 договор`
            }
        } else {
            // Когда количество не выбрано, используем минимальную скидку и оригинальную цену минимального тарифа
            // Находим тариф с минимальной ценой (оригинальной, без скидки)
            const minPriceTariff = tariffs.length > 0 ? tariffs.reduce((min, tariff) => {
                return tariff.price < min.price ? tariff : min
            }) : null
            
            if (minPriceTariff) {
                const minDiscount = getMinDiscount()
                const originalPrice = minPriceTariff.price
                const finalPrice = applyDiscountToPrice(originalPrice, minDiscount)
                const pricePerDoc = finalPrice / minPriceTariff.prepaid_documents_count / 100
                
                return {
                    name: 'На выбор',
                    badge: 'Выбираете сами',
                    period: baseTariff?.period === 'year' ? '1 год' : '1 месяц',
                    totalPrice: `${Math.round(finalPrice / 100)} ₽`, // Цена со скидкой (оригинальная будет показана зачеркнутой)
                    pricePerDocument: `от ${formatPrice(pricePerDoc)} ₽ за 1 договор`
                }
            }
            
            return {
                name: 'На выбор',
                badge: 'Выбираете сами',
                period: baseTariff?.period === 'year' ? '1 год' : '1 месяц',
                totalPrice: '0 ₽',
                pricePerDocument: '0 ₽ за 1 договор'
            }
        }
    }

    function handleInfoClick(event: React.MouseEvent) {
        event.stopPropagation()
        setShowInfoPopup(true)
    }

    function renderTariffOption(
        tariff: any,
        isSelected: boolean,
        onClick: () => void,
        displayData: TariffDisplayData
    ) {
        if (!tariff) {
            return null
        }
        
        const discount = tariff.discount || 0
        const originalPrice = tariff.price || 0
        const finalPrice = applyDiscountToPrice(originalPrice, discount)
        const isMinDiscount = tariff.isMinDiscount || false // Флаг для "от -X%"
        // Показываем цену со скидкой если есть скидка и оригинальная цена (для всех пакетов, включая индивидуальный)
        // Для индивидуального тарифа без выбора тоже показываем обе цены
        const showDiscountPrice = discount > 0 && originalPrice > 0
        
        return (
            <div 
                key={tariff._id || 'custom'}
                className={`package-size-option ${isSelected ? 'selected' : ''}`}
                onClick={onClick}
            >
                <div className="package-name-container">
                    <div className="package-name">{displayData.name}</div>
                    {discount > 0 && (
                        <div className="discount-badge-inline">
                            {isMinDiscount ? `от -${discount}%` : `-${discount}%`}
                        </div>
                    )}
                    {isInstallmentPackage(tariff) && (
                        <div className="installment-badge" data-testid="installment-badge">
                            Рассрочка
                        </div>
                    )}
                </div>
                <div className="package-badge">{displayData.badge}</div>
                <div className="expiration-info">
                    Срок {displayData.period}
                    <InfoButton onClick={handleInfoClick} size="small" />
                </div>
                <div className="divider"></div>
                <div className="price-section">
                    <div className="total-price">
                        {showDiscountPrice && (
                            <div className="original-price">
                                {(originalPrice / 100).toFixed(0)} ₽
                            </div>
                        )}
                        <div className={showDiscountPrice ? "discounted-price" : ""}>
                            {displayData.totalPrice}
                        </div>
                    </div>
                    <div className="price-per-document">
                        {displayData.pricePerDocument}
                    </div>
                    {isInstallmentPackage(tariff) && finalPrice > 0 && (
                        <div className="installment-note" data-testid="installment-note">
                            3 платежа по {formatPrice(firstInstallmentKopecks(finalPrice) / 100)} ₽
                        </div>
                    )}
                </div>
            </div>
        )
    }

    return (
        <div className="tariff-packages-container">
            <div className="package-sizes">
                {/* Фиксированные пакеты */}
                {tariffs.map((tariff) => 
                    renderTariffOption(
                        tariff,
                        isInCart(tariff._id),
                        () => onAddToCart(tariff),
                        createDisplayDataFromTariff(tariff)
                    )
                )}
                
                {/* Опция "Индивидуальный" */}
                {(() => {
                    const customData = getCustomPackageData()
                    const baseTariff = tariffs[0]
                    const isCustomSelected = isInCart('custom')
                    
                    // Создаем искусственный тариф для индивидуального пакета
                    const artificialTariff = createArtificialTariff(baseTariff, customData)
                    
                    return (
                        <div key="custom-tariff-container">
                            {renderTariffOption(
                                artificialTariff,
                                isCustomSelected,
                                () => onAddToCart(artificialTariff),
                                createDisplayDataFromCustom(customData, baseTariff)
                            )}
                            
                        </div>
                    )
                })()}
            </div>

            {/* Попап с информацией о сроке действия */}
            {showInfoPopup && (
                <Popup 
                    onClose={() => setShowInfoPopup(false)}
                    maxWidth="400px"
                    padding="24px"
                >
                    <div className="info-popup-content">
                        <h3 className="H3_21_v2" style={{ marginBottom: '16px' }}>
                            Срок действия тарифа
                        </h3>
                        <div className="Body_16_v2" style={{ marginBottom: '16px' }}>
                            Срок действия тарифа означает период, в течение которого вы можете использовать купленные документы:
                        </div>
                        <ul style={{ marginBottom: '16px', paddingLeft: '20px' }}>
                            <li className="Body_16_v2">
                                <strong>Годовой тариф</strong> - документы доступны в течение 1 года с момента покупки
                            </li>
                        </ul>
                        <div className="Body_16_v2_secondary">
                            После истечения срока действия неиспользованные документы сгорают и не подлежат восстановлению.
                        </div>
                    </div>
                </Popup>
            )}
        </div>
    )
}

export default TariffCards
